import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  RocCalculatorInput,
  RocCalculatorModelEnum,
  RocModel,
  RocModelDto,
  RocModelService,
  UserModel,
  UserModelDto
} from '@lib/data-access-domain';
import {
  AutoCompleteModel,
  Institution,
  Location,
  OptimizedLoansMapping,
  OptimizedLoansMappingQueryVariables,
  PercentileCareerDistribution,
  RocCalculator,
  RocCalculatorOutput
} from '@lib/utils';
import { environment } from '@environment';
import { combineLatest, forkJoin, Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GraphqlApi } from 'src/app/core/api/graphql.api';

import { HighSchoolSalaryService } from './high-school-salary.service';
import { convertHighSchoolSalaryToSalaryAnalysis } from './utils/high-school-salary-to-salary-analysis.util';
import { getCurrentCalculatorOutput } from './utils/get-current-calculator-output.util';
import { getGoalCalculatorOutput } from './utils/get-goal-calculator-output.util';

@Injectable()
export class WidgetService {
  private readonly options: Record<string, unknown> = {
    fetchPolicy: 'cache-first'
  };
  private readonly silentOptions: Record<string, unknown> = {
    context: {
      headers: { 'x-silent-header': 'true' }
    },
    fetchPolicy: 'cache-first'
  };

  public constructor(
    private graphql: GraphqlApi,
    private httpClient: HttpClient,
    private highSchoolSalaryService: HighSchoolSalaryService
  ) {}

  public calculate(input: RocCalculatorInput): Observable<RocCalculator> {
    return this.httpClient.post<RocCalculator>(environment.rocCalculatorApi, input);
  }

  public getLocation(location: AutoCompleteModel): Observable<Location> {
    return this.graphql
      .exchangeAutoCompleteForLocationGQL(
        {
          autoCompleteModel: location,
          zipCode: location.id,
          distance: 1
        },
        this.silentOptions
      )
      .pipe(map(res => res.exchangeAutoCompleteForLocation as Location));
  }

  public getSalaryAnalysis(location: AutoCompleteModel, cipCode?: string): Observable<PercentileCareerDistribution> {
    return combineLatest([
      this.graphql.exchangeAutoCompleteForLocationGQL(
        {
          autoCompleteModel: location,
          zipCode: location.id,
          distance: 1
        },
        this.silentOptions
      ),
      cipCode
        ? this.graphql
            .cipSalaryAnalysisByZipCodeGQL(
              {
                cipCode,
                zipCode: location.id
              },
              this.silentOptions
            )
            .pipe(
              catchError(() => of({ data: { cipSalaryAnalysisByZipCode: null } })),
              map(data => data.cipSalaryAnalysisByZipCode)
            )
        : this.highSchoolSalaryService
            .getHighSchoolSalary()
            .pipe(map(data => convertHighSchoolSalaryToSalaryAnalysis(data, location.name)))
    ]).pipe(
      map(([locationRes, salaryRes]) =>
        RocModelService.getTargetSalaryAnalysis(
          locationRes.exchangeAutoCompleteForLocation.medianAnnualIndividualSalary,
          salaryRes
        )
      )
    );
  }

  public getInstitutionById(institutionId: string, cipCode: string): Observable<Institution> {
    return this.graphql
      .institutionByUnitIdGQL({ unitId: institutionId, cipCode }, this.options)
      .pipe(map(res => (res as any).institution));
  }

  public runCalculator(rocModel: RocModelDto, userModel: UserModelDto): Observable<RocCalculatorOutput[]> {
    return forkJoin([
      this.runCalculatorByType(RocCalculatorModelEnum.CURRENT, rocModel, userModel),
      this.runCalculatorByType(RocCalculatorModelEnum.GOAL, rocModel, userModel)
    ]);
  }

  public getOptimizedLoansMapping(
    optimizedLoansMappingQueryVariables: OptimizedLoansMappingQueryVariables
  ): Observable<OptimizedLoansMapping> {
    return this.graphql
      .optimizedLoansMappingGQL(optimizedLoansMappingQueryVariables, this.options)
      .pipe(map((data: { optimizedLoansMapping: OptimizedLoansMapping }) => data.optimizedLoansMapping));
  }

  private runCalculatorByType(
    rocCalculatorEnum: RocCalculatorModelEnum,
    rocModel: RocModelDto,
    userModel: UserModelDto
  ): Observable<RocCalculatorOutput> {
    let input: RocCalculatorInput;
    if (rocCalculatorEnum === RocCalculatorModelEnum.CURRENT) {
      input = RocModelService.toCalculatorInputForCurrentState(rocModel as unknown as RocModel, userModel as UserModel);
    } else {
      input = RocModelService.toCalculatorInputForGoalState(rocModel as unknown as RocModel, userModel as UserModel);
    }

    input.grantOrScholarshipAidByYear = input.grantOrScholarshipAidByYear.map(it => it || 0);

    return this.calculate(input).pipe(
      map(it =>
        rocCalculatorEnum === RocCalculatorModelEnum.CURRENT
          ? getCurrentCalculatorOutput(it)
          : getGoalCalculatorOutput(it)
      )
    );
  }
}
